import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { FiEdit, FiMapPin, FiPhone, FiPlus, FiCheck, FiTrash2, FiX } from '../../../assets/icons/vander';
import Navbar from "../../../components/navbar";
import Usertab from "../../../components/user-tab";
import Footer from "../../../components/footer";
import Switcher from "../../../components/switcher";
import ScrollToTop from "../../../components/scroll-to-top";
import toast from "react-hot-toast";

export default function UserBilling() {
  const [loading, setLoading] = useState(false);
  const [userAddressData, setUserAddressData] = useState([]);
  const [addressFetchError, setAddressFetchError] = useState(null);
  const [openAdd, setOpenAdd] = useState(false);
  const [editAddress, setEditAddress] = useState(null);
  const [newAddress, setNewAddress] = useState({
    full_name: "",
    mobile_number: "",
    care_of: "",
    address1: "",
    address2: "",
    landmark: "",
    pincode: "",
    city: "",
    district: "",
    state: "",
    country: "",
  });
  const [pincodeError, setPincodeError] = useState(null);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    addressId: null,
    addressName: ""
  });
  
  const customerId = localStorage.getItem("customer_id");

  // Fetch addresses from API
  const fetchAddresses = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/address/?customer_id=${customerId}`
      );
      setUserAddressData(response.data);
      setAddressFetchError(null);
    } catch (error) {
      console.error("Error fetching addresses:", error);
      setAddressFetchError("Failed to load addresses. Please try again.");
      setUserAddressData([]);
    }
  };

  useEffect(() => {
    if (customerId) {
      fetchAddresses();
    }
  }, [customerId]);

  // Handle pincode input and API call
  const handlePincodeChange = async (e) => {
    const pincode = e.target.value;
    setNewAddress({ ...newAddress, pincode });

    if (pincode.length === 6 && /^\d{6}$/.test(pincode)) {
      setPincodeError(null);
      try {
        const response = await fetch(
          `https://api.postalpincode.in/pincode/${pincode}`
        );
        const data = await response.json();

        if (data[0]?.Status === "Success" && data[0]?.PostOffice?.length > 0) {
          const postOffice = data[0].PostOffice[0];
          setNewAddress((prev) => ({
            ...prev,
            city: postOffice.Name,
            district: postOffice.District,
            state: postOffice.State,
            country: postOffice.Country,
          }));
        } else {
          setPincodeError("Invalid pincode or no data found.");
          setNewAddress((prev) => ({
            ...prev,
            city: "",
            district: "",
            state: "",
            country: "",
          }));
        }
      } catch (error) {
        console.error("Error fetching pincode data:", error);
        setPincodeError("Error fetching pincode data. Please try again.");
        setNewAddress((prev) => ({
          ...prev,
          city: "",
          district: "",
          state: "",
          country: "",
        }));
      }
    } else if (pincode.length > 0 && pincode.length !== 6) {
      setPincodeError("Pincode must be 6 digits.");
    } else {
      setPincodeError(null);
      setNewAddress((prev) => ({
        ...prev,
        city: "",
        district: "",
        state: "",
        country: "",
      }));
    }
  };

  // Handle mobile number validation
  const handleMobileNumberChange = (e) => {
    const mobile_number = e.target.value;
    setNewAddress({ ...newAddress, mobile_number });
  };

  // Handle save or update address API call
  const handleSaveAddress = async () => {
    if (
      !newAddress.full_name ||
      !newAddress.mobile_number ||
      !newAddress.address1 ||
      !newAddress.pincode ||
      !newAddress.city ||
      !newAddress.district ||
      !newAddress.state ||
      !newAddress.country
    ) {
      toast.error("Please fill all required fields.", {
        style: { borderRadius: "10px", background: "#333", color: "#fff" },
      });
      return;
    }
    if (!/^\d{10}$/.test(newAddress.mobile_number)) {
      toast.error("Please enter a valid 10-digit mobile number.", {
        style: { borderRadius: "10px", background: "#333", color: "#fff" },
      });
      return;
    }
    if (pincodeError) {
      toast.error("Please enter a valid pincode.", {
        style: { borderRadius: "10px", background: "#333", color: "#fff" },
      });
      return;
    }

    const customerId = localStorage.getItem("customer_id");
    if (!customerId) {
      toast.error("Customer ID not found. Please log in again.", {
        style: { borderRadius: "10px", background: "#333", color: "#fff" },
      });
      return;
    }

    const payload = {
      customer_id: customerId,
      country: newAddress.country,
      care_of: newAddress.care_of || null,
      full_name: newAddress.full_name,
      mobile_no: parseInt(newAddress.mobile_number),
      pincode: parseInt(newAddress.pincode),
      address1: newAddress.address1,
      address2: newAddress.address2 || "",
      landmark: newAddress.landmark || "",
      town: newAddress.city,
      district: newAddress.district,
      state: newAddress.state,
    };

    try {
      setLoading(true);
      if (editAddress) {
        await axios.put(
          `${process.env.REACT_APP_API_BASE_URL}/address/?address_id=${editAddress.address_id}`,
          payload
        );
        toast.success("Address updated successfully", {
          style: { borderRadius: "10px", background: "#333", color: "#fff" },
        });
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/address/`,
          payload
        );
        toast.success("Address added successfully", {
          style: { borderRadius: "10px", background: "#333", color: "#fff" },
        });
      }
      
      // Refetch addresses after adding or updating
      await fetchAddresses();
      
      setOpenAdd(false);
      setEditAddress(null);
      setNewAddress({
        full_name: "",
        mobile_number: "",
        care_of: "",
        address1: "",
        address2: "",
        landmark: "",
        pincode: "",
        city: "",
        district: "",
        state: "",
        country: "",
      });
      setPincodeError(null);
    } catch (error) {
      console.error("Error saving/updating address:", error);
      toast.error(
        `Failed to ${
          editAddress ? "update" : "save"
        } address. Please try again.`,
        {
          style: { borderRadius: "10px", background: "#333", color: "#fff" },
        }
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle delete address API call
  const handleDeleteAddress = async () => {
    const { addressId } = deleteModal;
    
    try {
      setLoading(true);
      await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/address/?customer_id=${customerId}&address_id=${addressId}`
      );
      toast.success("Address deleted successfully", {
        style: { borderRadius: "10px", background: "#333", color: "#fff" },
      });
      
      // Refetch addresses after deletion
      await fetchAddresses();
      
      setDeleteModal({ isOpen: false, addressId: null, addressName: "" });
    } catch (error) {
      console.error("Error deleting address:", error);
      toast.error("Failed to delete address. Please try again.", {
        style: { borderRadius: "10px", background: "#333", color: "#fff" },
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle set default address
  const handleSetDefault = async (address_id) => {
    try {
      setLoading(true);
      
      // Use the correct API endpoint and payload
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/address/?address_id=${address_id}`,
        {
          default: true
        }
      );
      
      // Refetch addresses after setting default
      await fetchAddresses();
      
      toast.success("Default address updated successfully", {
        style: { borderRadius: "10px", background: "#333", color: "#fff" },
      });
    } catch (error) {
      console.error("Error setting default address:", error);
      toast.error("Failed to set default address. Please try again.", {
        style: { borderRadius: "10px", background: "#333", color: "#fff" },
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle edit address
  const handleEditAddress = (address) => {
    setNewAddress({
      full_name: address.full_name,
      mobile_number: address.mobile_no.toString(),
      care_of: address.care_of || "",
      address1: address.address1,
      address2: address.address2 || "",
      landmark: address.landmark || "",
      pincode: address.pincode.toString(),
      city: address.town,
      district: address.district,
      state: address.state,
      country: address.country,
    });
    setEditAddress(address);
    setOpenAdd(true);
    setPincodeError(null);
  };

  // Handle delete confirmation
  const handleDeleteConfirmation = (address) => {
    setDeleteModal({
      isOpen: true,
      addressId: address.address_id,
      addressName: address.full_name
    });
  };

  const onClose = () => {
    setOpenAdd(false);
    setEditAddress(null);
    setNewAddress({
      full_name: "",
      mobile_number: "",
      care_of: "",
      address1: "",
      address2: "",
      landmark: "",
      pincode: "",
      city: "",
      district: "",
      state: "",
      country: "",
    });
    setPincodeError(null);
  };

  return (
    <>
      <Navbar navClass="defaultscroll is-sticky"/>
      <section className="relative lg:pb-24 pb-16 md:mt-[84px] mt-[70px]">
        <div className="md:container container-fluid relative">
          <div className="relative overflow-hidden md:rounded-md shadow dark:shadow-gray-700 h-52 bg-[url('../../assets/images/hero/pages.jpg')] bg-center bg-no-repeat bg-cover"></div>
        </div>

        <div className="container relative md:mt-24 mt-16">
          <div className="md:flex">
            <Usertab/>

            <div className="lg:w-3/4 md:w-2/3 md:px-3 mt-6 md:mt-0">
              <div className="p-6 rounded-md shadow dark:shadow-gray-800 bg-white dark:bg-slate-900">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-semibold">shipping address</h3>
                  <button 
                    onClick={() => setOpenAdd(true)}
                    className="flex items-center gap-1 text-green-600 hover:text-green-700"
                  >
                    <FiPlus className="h-4 w-4" /> Add New Address
                  </button>
                </div>
                
                {addressFetchError ? (
                  <p className="text-red-500 text-sm">{addressFetchError}</p>
                ) : userAddressData.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No addresses found. Add a new address to get started.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {userAddressData.map((address) => (
                      <div 
                        key={address.address_id}
                        className={`p-4 border rounded-lg ${address.default ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="font-medium">{address.full_name}</h4>
                              {address.default && (
                                <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                                  Default
                                </span>
                              )}
                            </div>
                            
                            <ul className="list-none">
                              <li className="flex items-start mb-1">
                                <FiMapPin className="size-4 me-2 mt-1 text-gray-500" />
                                <p className="text-sm text-gray-600">
                                  {address.address1}, {address.address2 && `${address.address2}, `}
                                  {address.landmark && `${address.landmark}, `}
                                  {address.town}, {address.district}, {address.state}, 
                                  {address.pincode}, {address.country}
                                </p>
                              </li>
                              
                              <li className="flex items-center">
                                <FiPhone className="size-4 me-2 text-gray-500" />
                                <p className="text-sm text-gray-600">+{address.mobile_no}</p>
                              </li>
                              
                              {address.care_of && (
                                <li className="flex items-center mt-1">
                                  <span className="text-sm text-gray-600">Care of: {address.care_of}</span>
                                </li>
                              )}
                            </ul>
                          </div>
                          
                          <div className="flex flex-col gap-2">
                            <button
                              onClick={() => handleEditAddress(address)}
                              className="text-gray-600 hover:text-green-500"
                              title="Edit"
                            >
                              <FiEdit className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleDeleteConfirmation(address)}
                              className="text-gray-600 hover:text-red-500"
                              title="Delete"
                            >
                              <FiTrash2 className="h-5 w-5" />
                            </button>
                            {!address.default && (
                              <button
                                onClick={() => handleSetDefault(address.address_id)}
                                className="text-gray-600 hover:text-green-500"
                                title="Set as Default"
                              >
                                <FiCheck className="h-5 w-5" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Address Form Section */}
                {openAdd && (
                  <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold">
                        {editAddress ? "Edit Address" : "Add New Address"}
                      </h3>
                      <button 
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <FiX className="h-5 w-5" />
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          className="w-full p-2 border rounded dark:bg-slate-800 dark:border-gray-700"
                          value={newAddress.full_name}
                          onChange={(e) =>
                            setNewAddress({
                              ...newAddress,
                              full_name: e.target.value,
                            })
                          }
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Mobile Number *
                        </label>
                        <input
                          type="text"
                          className={`w-full p-2 border rounded dark:bg-slate-800 dark:border-gray-700 ${
                            newAddress.mobile_number &&
                            !/^\d{10}$/.test(newAddress.mobile_number)
                              ? "border-red-500"
                              : ""
                          }`}
                          value={newAddress.mobile_number}
                          onChange={handleMobileNumberChange}
                        />
                        {newAddress.mobile_number &&
                          !/^\d{10}$/.test(newAddress.mobile_number) && (
                            <p className="text-red-500 text-xs mt-1">
                              Mobile number must be 10 digits.
                            </p>
                          )}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Care Of (Optional)
                        </label>
                        <input
                          type="text"
                          className="w-full p-2 border rounded dark:bg-slate-800 dark:border-gray-700"
                          value={newAddress.care_of}
                          onChange={(e) =>
                            setNewAddress({
                              ...newAddress,
                              care_of: e.target.value,
                            })
                          }
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Pincode *
                        </label>
                        <input
                          type="text"
                          className={`p-2 border rounded w-full dark:bg-slate-800 dark:border-gray-700 ${
                            pincodeError ? "border-red-500" : ""
                          }`}
                          value={newAddress.pincode}
                          onChange={handlePincodeChange}
                        />
                        {pincodeError && (
                          <p className="text-red-500 text-xs mt-1">
                            {pincodeError}
                          </p>
                        )}
                      </div>
                      
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Address Line 1 *
                        </label>
                        <input
                          type="text"
                          className="w-full p-2 border rounded dark:bg-slate-800 dark:border-gray-700"
                          value={newAddress.address1}
                          onChange={(e) =>
                            setNewAddress({
                              ...newAddress,
                              address1: e.target.value,
                            })
                          }
                        />
                      </div>
                      
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Address Line 2
                        </label>
                        <input
                          type="text"
                          className="w-full p-2 border rounded dark:bg-slate-800 dark:border-gray-700"
                          value={newAddress.address2}
                          onChange={(e) =>
                            setNewAddress({
                              ...newAddress,
                              address2: e.target.value,
                            })
                          }
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Landmark
                        </label>
                        <input
                          type="text"
                          className="w-full p-2 border rounded dark:bg-slate-800 dark:border-gray-700"
                          value={newAddress.landmark}
                          onChange={(e) =>
                            setNewAddress({
                              ...newAddress,
                              landmark: e.target.value,
                            })
                          }
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          City *
                        </label>
                        <input
                          type="text"
                          className="p-2 border rounded w-full dark:bg-slate-800 dark:border-gray-700"
                          value={newAddress.city}
                          onChange={(e) =>
                            setNewAddress({
                              ...newAddress,
                              city: e.target.value,
                            })
                          }
                          disabled={newAddress.city && !pincodeError}
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          District *
                        </label>
                        <input
                          type="text"
                          className="p-2 border rounded w-full dark:bg-slate-800 dark:border-gray-700"
                          value={newAddress.district}
                          onChange={(e) =>
                            setNewAddress({
                              ...newAddress,
                              district: e.target.value,
                            })
                          }
                          disabled={newAddress.district && !pincodeError}
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          State *
                        </label>
                        <input
                          type="text"
                          className="p-2 border rounded w-full dark:bg-slate-800 dark:border-gray-700"
                          value={newAddress.state}
                          onChange={(e) =>
                            setNewAddress({
                              ...newAddress,
                              state: e.target.value,
                            })
                          }
                          disabled={newAddress.state && !pincodeError}
                        />
                      </div>
                      
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Country *
                        </label>
                        <input
                          type="text"
                          className="p-2 border rounded w-full dark:bg-slate-800 dark:border-gray-700"
                          value={newAddress.country}
                          onChange={(e) =>
                            setNewAddress({
                              ...newAddress,
                              country: e.target.value,
                            })
                          }
                          disabled={newAddress.country && !pincodeError}
                        />
                      </div>
                    </div>
                    
                    <div className="flex justify-end gap-3 mt-6">
                      <button
                        className="px-4 py-2 border rounded text-gray-600 hover:bg-gray-100"
                        onClick={onClose}
                      >
                        Cancel
                      </button>
                      <button
                        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                        onClick={handleSaveAddress}
                      >
                        {editAddress ? "Update Address" : "Save Address"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <>
          {/* Backdrop with reduced opacity */}
          <div 
            className="fixed inset-0 z-40"
            style={{ 
              backgroundColor: 'rgba(0, 0, 0, 0.25)',
              backdropFilter: 'blur(2px)'
            }}
          ></div>
          
          {/* Modal Content */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-md transform transition-all">
              <div className="p-6">
                <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
                  <FiTrash2 className="h-6 w-6 text-red-600" />
                </div>
                
                <h3 className="text-lg font-medium text-center mb-2">Delete Address</h3>
                <p className="text-sm text-gray-500 text-center mb-6">
                  Are you sure you want to delete the address for <span className="font-medium">{deleteModal.addressName}</span>? This action cannot be undone.
                </p>
                
                <div className="flex justify-center gap-3">
                  <button
                    className="px-4 py-2 border rounded text-gray-600 hover:bg-gray-100"
                    onClick={() => setDeleteModal({ isOpen: false, addressId: null, addressName: "" })}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                    onClick={handleDeleteAddress}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      
      <Footer/>
      <Switcher/>
      <ScrollToTop/>
    </>
  )
}